import {
  Button,
  Heading,
  Icon,
  Input,
  Modal,
  Spinner,
  Tag,
  Text,
  Tooltip,
} from "@ttc3k/trekker";
import { Archive, AtSign, PageFlip, Plus, Xmark } from "iconoir-react";
import { memo, useMemo, useEffect, useRef, useState, useTransition } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useGlobalNotesModal } from "./useGlobalNotesModal";
import { useNotesContext } from "shared/contextProviders";
import type { NoteManyQuery } from "gql/generated";
import { Collapsible, EmptyState, NoteCard } from "shared/components";
import { motion, Variants } from "framer-motion";
import { format, parse } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { notesEntityFilters } from "./constants";
import type { TimelineNoteItem } from "./useGlobalNotesModal";
import { DATE_PATTERN_SIMPLE } from "shared/utils";

/** Virtual list: only visible groups in DOM; isolated so only this re-renders on scroll */
/** Conservative estimate so variable-height cards don’t overlap before measurement */
const DATE_ROW_HEIGHT_PX = 40;
const DATE_LINE_GAP_PX = 12;
const DATE_LABEL_LINE_GAP_PX = 6;
const CARD_ROW_HEIGHT_PX = 220;
const SCROLL_GUTTER_PX = 20;
const CARD_GAP_PX = 12;
const OVERSCAN = 7;

type FlatRow =
  | { type: "date"; groupByDate: string; formattedDate: string }
  | {
      type: "dateWithCard";
      groupByDate: string;
      formattedDate: string;
      item: TimelineNoteItem;
    }
  | { type: "card"; item: TimelineNoteItem; groupByDate: string };

type GlobalNotesVirtualListProps = {
  flatRows: FlatRow[];
  onCloseModal: () => void;
  exitingNoteId: string | null;
  onDeleteRequested: (note: NoteManyQuery["noteMany"][number]) => void;
  onExitAnimationComplete: () => void;
  suggestionsPortalHost?: HTMLElement | null;
};

function getRowHeightPx(row: FlatRow): number {
  return row.type === "date"
    ? DATE_ROW_HEIGHT_PX
    : CARD_ROW_HEIGHT_PX + CARD_GAP_PX;
}

const GlobalNotesVirtualList = memo(function GlobalNotesVirtualList({
  flatRows,
  onCloseModal,
  exitingNoteId,
  onDeleteRequested,
  onExitAnimationComplete,
  suggestionsPortalHost,
}: GlobalNotesVirtualListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => getRowHeightPx(flatRows[i]!),
    overscan: OVERSCAN,
  });

  return (
    <div
      ref={scrollRef}
      style={{
        flex: 1,
        minHeight: 400,
        overflow: "auto",
        paddingRight: SCROLL_GUTTER_PX,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = flatRows[virtualRow.index]!;
          const key =
            row.type === "date"
              ? `date-${row.groupByDate}`
              : row.type === "dateWithCard"
                ? `dateWithCard-${row.groupByDate}-${row.item.note._id}`
                : `card-${row.item.note._id}`;
          return (
            <div
              key={key}
              data-index={virtualRow.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${getRowHeightPx(row)}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {row.type === "date" ? (
                <Grid
                  gridTemplateColumns="48px 1fr"
                  gridGap="150"
                  justifyItems="stretch"
                  alignItems="center"
                  w="full"
                  h="full"
                >
                  <Flex flexDir="column" alignItems="center" w="full" h="full">
                    <Box w="full" style={{ height: DATE_LINE_GAP_PX }} />
                    <Text visual="smallTag" color="text.light">
                      {row.formattedDate}
                    </Text>
                    <Box w="full" style={{ height: DATE_LABEL_LINE_GAP_PX }} />
                    <Box
                      border="1px solid"
                      borderColor="border.light"
                      borderLeft="none"
                      alignSelf="center"
                      w="0"
                      flex="1"
                      minH="0"
                    />
                  </Flex>
                  <Box />
                </Grid>
              ) : row.type === "dateWithCard" ? (
                (() => {
                  const nextRow = flatRows[virtualRow.index + 1];
                  const nextRowIsDate = nextRow?.type === "date";
                  return (
                    <Grid
                      gridTemplateColumns="48px 1fr"
                      gridGap="150"
                      justifyItems="flex-start"
                      alignItems="stretch"
                      w="full"
                      h="full"
                    >
                      <Flex flexDir="column" alignItems="center" w="full" h="full">
                        <Text visual="smallTag" color="text.light">
                          {row.formattedDate}
                        </Text>
                        <Box w="full" style={{ height: DATE_LABEL_LINE_GAP_PX }} />
                        <Box
                          border="1px solid"
                          borderColor="border.light"
                          borderLeft="none"
                          alignSelf="center"
                          w="0"
                          flex="1"
                          minH="0"
                          style={
                            nextRowIsDate
                              ? { marginBottom: DATE_LINE_GAP_PX }
                              : undefined
                          }
                        />
                      </Flex>
                      <Flex
                        flexDir="column"
                        w="100%"
                        minW="0"
                        h="full"
                        py="0"
                        alignItems="flex-start"
                      >
                        <NoteCard
                          initialMode="view"
                          note={row.item.note}
                          onCloseModal={onCloseModal}
                          isExiting={row.item.note._id === exitingNoteId}
                          onDeleteRequested={onDeleteRequested}
                          onExitAnimationComplete={onExitAnimationComplete}
                          messageMaxHeight={CARD_ROW_HEIGHT_PX}
                          style={{ width: "100%" }}
                          suggestionsPortalHost={suggestionsPortalHost}
                        />
                      </Flex>
                    </Grid>
                  );
                })()
              ) : (
                (() => {
                  const nextRow = flatRows[virtualRow.index + 1];
                  const nextRowIsDate = nextRow?.type === "date";
                  return (
                    <Grid
                      gridTemplateColumns="48px 1fr"
                      gridGap="150"
                      justifyItems="flex-start"
                      alignItems="stretch"
                      w="full"
                      h="full"
                    >
                      <Flex flexDir="column" alignItems="center" w="full" h="full">
                        <Box
                          border="1px solid"
                          borderColor="border.light"
                          borderLeft="none"
                          alignSelf="center"
                          w="0"
                          flex="1"
                          minH="0"
                          style={
                            nextRowIsDate
                              ? { marginBottom: DATE_LINE_GAP_PX }
                              : undefined
                          }
                        />
                      </Flex>
                      <Flex flexDir="column" w="100%" minW="0" h="full" py="0">
                        <NoteCard
                          initialMode="view"
                          note={row.item.note}
                          onCloseModal={onCloseModal}
                          isExiting={row.item.note._id === exitingNoteId}
                          onDeleteRequested={onDeleteRequested}
                          onExitAnimationComplete={onExitAnimationComplete}
                          messageMaxHeight={CARD_ROW_HEIGHT_PX}
                          style={{ width: "100%" }}
                          suggestionsPortalHost={suggestionsPortalHost}
                        />
                      </Flex>
                    </Grid>
                  );
                })()
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

type GlobalNotesModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const GlobalNotesModal = ({
  isOpen,
  onClose,
}: GlobalNotesModalProps) => {
  const { t } = useTranslation("core", { keyPrefix: "NOTES.GLOBAL_NOTES" });
  const { t: tCore } = useTranslation("core");
  const { deleteNote } = useNotesContext();
  const {
    filteredNotesGroupedByDate,
    loading,
    search,
    onSearch,
    onToggleArchivedNotes,
    onToggleMyNotes,
    onNewNoteSaveSuccess,
    onToggleEntityFilter,
    filtersOn,
    currentEntityFilter,
    isAddingNote,
    setIsAddingNote,
  } = useGlobalNotesModal();

  const [isPending, startTransition] = useTransition();
  const [exitingNoteId, setExitingNoteId] = useState<string | null>(null);
  const mentionsPortalRef = useRef<HTMLDivElement | null>(null);
  const [mentionsPortalReady, setMentionsPortalReady] = useState(false);

  const handleDeleteRequested = (note: NoteManyQuery["noteMany"][number]) => {
    setExitingNoteId(note._id);
  };

  const handleExitAnimationComplete = () => {
    if (!exitingNoteId) return;
    deleteNote(exitingNoteId);
  };

  // Clear exitingNoteId only after the note is actually gone from the list.
  // If we cleared in deleteNote().finally(), the list might not have updated yet
  // (refetch in flight), so the card would briefly reappear as editable.
  useEffect(() => {
    if (!exitingNoteId) return;
    const noteStillInList = filteredNotesGroupedByDate.some((group) =>
      group.items.some((item) => item.note._id === exitingNoteId),
    );
    if (!noteStillInList) setExitingNoteId(null);
  }, [exitingNoteId, filteredNotesGroupedByDate]);

  // Reset mentions portal when modal closes so we don't pass a detached node.
  useEffect(() => {
    if (!isOpen) setMentionsPortalReady(false);
  }, [isOpen]);

  const conditionalArchiveButtonProps = {
    ...(filtersOn.includes("archived") ? { "data-active": true } : {}),
  };
  const conditionalMyNotesButtonProps = {
    ...(filtersOn.includes("myNotes") ? { "data-active": true } : {}),
  };

  const flatRows: FlatRow[] = useMemo(() => {
    const rows: FlatRow[] = [];
    for (const group of filteredNotesGroupedByDate) {
      const itemDate = format(
        parse(group.groupByDate, DATE_PATTERN_SIMPLE, new Date()),
        DATE_PATTERN_SIMPLE,
      );
      const today = format(new Date(), DATE_PATTERN_SIMPLE);
      const formattedDate =
        itemDate === today
          ? tCore("TODAY")
          : format(
              toZonedTime(
                parse(group.groupByDate, DATE_PATTERN_SIMPLE, new Date()),
                "UTC",
              ),
              "MMM d",
            );
      if (group.items.length === 0) {
        rows.push({
          type: "date",
          groupByDate: group.groupByDate,
          formattedDate,
        });
      } else {
        rows.push({
          type: "dateWithCard",
          groupByDate: group.groupByDate,
          formattedDate,
          item: group.items[0]!,
        });
        for (let i = 1; i < group.items.length; i++) {
          rows.push({
            type: "card",
            item: group.items[i]!,
            groupByDate: group.groupByDate,
          });
        }
      }
    }
    return rows;
  }, [filteredNotesGroupedByDate, tCore]);

  // Defer mounting the notes list until after panel is open so open feels instant
  const [notesReady, setNotesReady] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setNotesReady(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      setNotesReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      placement={"rightLargePane"}
      onOpenChange={onClose}
      closeTriggerVisual="unstyled"
      closeTriggerPlacement="whereabouts"
      closeTriggerProps={{
        className: css({
          bg: "bg.lighter",
          color: "white",
          borderRadius: "full",
          h: "24px",
          w: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
          p: "50",
          _hover: {
            bg: "bg.light",
          },
          _active: {
            bg: "bg.mid",
          },
        }),
        style: {
          transition: "background-color 0.2s ease",
        },
        children: (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
            <Icon Element={Xmark} size={"sm"} color="icon.inverse" />
          </span>
        ),
      }}
      portal
    >
      {/* Portal target for mentions dropdown (in body) so it is not clipped by modal overflow */}
      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={el => {
              mentionsPortalRef.current = el;
              if (el) setMentionsPortalReady(true);
            }}
            style={{
              position: "fixed",
              inset: 0,
              width: "100%",
              height: "100%",
              overflow: "visible",
              zIndex: 2147483647,
              pointerEvents: "none",
            }}
          />,
          document.body,
        )}
      <Flex
        flexDir={"column"}
        gap={"400"}
        px={"500"}
        pb={"500"}
        pt={"1000"}
        flex={"1"}
        minHeight={"0"}
        height={"100%"}
      >
        {/* Header */}
        <Flex flexDir={"column"} gap={"150"}>
          <Icon size={"xl"} Element={PageFlip} color={"icon.dark"} />
          <Flex flexDir={"column"} gap={"200"}>
            <Heading size={"h4"} color={"text.dark"}>
              {t("TITLE")}
            </Heading>
            <Text color={"text.mid"} fontWeight={"light"}>
              {t("DESCRIPTION")}
            </Text>
          </Flex>
        </Flex>

        {/* Filtering */}
        <Flex alignItems={"center"} gap={"150"}>
          <Input
            placeholder={t("SEARCH_PLACEHOLDER") + "..."}
            onChange={e => onSearch(e.target.value)}
            value={search}
          />
          <Flex gap={"150"} alignItems={"center"}>
            <Tooltip
              content={t("FILTERS.ARCHIVED")}
              positioning={{ placement: "top" }}
            >
              <Button
                mode={"light"}
                Icon={Archive}
                className={css({
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: "transparent",
                  transitionProperty:
                    "border-color, outline-color, background, color!",
                  color: "icon.light",
                  _hover: {
                    outlineColor: "border.dark",
                    borderColor: "border.dark",
                    color: "icon.dark",
                  },
                  _active: {
                    outlineColor: "border.dark",
                    borderColor: "border.dark",
                    color: "icon.dark",
                    bg: "bg.lightest",
                  },
                })}
                onClick={() => startTransition(onToggleArchivedNotes)}
                type={"button"}
                {...conditionalArchiveButtonProps}
              />
            </Tooltip>
            <Tooltip
              content={t("FILTERS.MY_NOTES")}
              positioning={{ placement: "top" }}
            >
              <Button
                mode={"light"}
                Icon={AtSign}
                className={css({
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: "transparent",
                  transitionProperty:
                    "border-color, outline-color, background, color!",
                  color: "icon.light",
                  _hover: {
                    outlineColor: "border.accent.magenta",
                    borderColor: "border.accent.magenta",
                    color: "icon.accent.magenta.mid",
                    bg: "bg.accent.magenta.lightest",
                  },
                  _active: {
                    outlineColor: "border.accent.magenta",
                    borderColor: "border.accent.magenta",
                    color: "icon.accent.magenta.mid",
                    bg: "bg.accent.magenta.lightest",
                  },
                })}
                onClick={() => startTransition(onToggleMyNotes)}
                type={"button"}
                {...conditionalMyNotesButtonProps}
              />
            </Tooltip>
            <Button
              visual={"accent.blue"}
              Icon={Plus}
              onClick={() => setIsAddingNote(true)}
            />
          </Flex>
        </Flex>
        <Flex alignItems={"center"} gap={"100"}>
          <Text visual={"smallTag"} color={"text.mid"}>
            {t("FILTERS.TITLE")}
          </Text>
          {notesEntityFilters.map(filter => {
            const isSelected = currentEntityFilter === filter.type;
            return (
              <motion.span
                key={filter.type}
                style={{ display: "inline-block" }}
                whileTap={{ scale: 0.94 }}
                transition={{ duration: 0.08 }}
              >
                <Tag
                  mode={isSelected ? "dark" : "light"}
                  onClick={() =>
                    startTransition(() => onToggleEntityFilter(filter.type))
                  }
                >
                  {t(filter.labelKey)}
                </Tag>
              </motion.span>
            );
          })}
        </Flex>

        {/* Add note */}
        <Collapsible
          open={isAddingNote}
          additionalVariantProps={
            {
              open: {
                display: "block",
              },
              closed: {
                display: "none",
              },
            } as Variants
          }
        >
          {isAddingNote && (
            <NoteCard
              initialMode={isAddingNote ? "edit" : "view"}
              onSaveSuccess={onNewNoteSaveSuccess}
              onCancel={() => setIsAddingNote(false)}
              suggestionsPortalHost={
                  mentionsPortalReady
                    ? mentionsPortalRef.current ?? undefined
                    : typeof document !== "undefined"
                      ? document.body
                      : undefined
                }
            />
          )}
        </Collapsible>

        {/* Notes Timeline: virtualized so only visible groups are in the DOM */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          {isPending && (
            <Flex
              position="absolute"
              inset="0"
              zIndex={1}
              alignItems="center"
              justifyContent="center"
              bg="bg.wa.light"
              opacity={0.9}
            >
              <Spinner width={"32px"} height={"32px"} />
            </Flex>
          )}
          {!notesReady ? (
            <Flex flex={1} minHeight={0} alignItems="center" justifyContent="center" py="1000">
              <Spinner width={"32px"} height={"32px"} />
            </Flex>
          ) : loading ? (
            <Spinner width={"32px"} height={"32px"} margin={"0 auto"} />
          ) : flatRows.length === 0 ? (
            <EmptyState text={t("EMPTY")} />
          ) : (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <GlobalNotesVirtualList
                flatRows={flatRows}
                onCloseModal={onClose}
                exitingNoteId={exitingNoteId}
                onDeleteRequested={handleDeleteRequested}
                onExitAnimationComplete={handleExitAnimationComplete}
                suggestionsPortalHost={
                  mentionsPortalReady
                    ? mentionsPortalRef.current ?? undefined
                    : typeof document !== "undefined"
                      ? document.body
                      : undefined
                }
              />
            </div>
          )}
        </div>
      </Flex>
    </Modal>
  );
};
